import { SendOutlined } from '@ant-design/icons'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin'
import { Button } from 'antd'
import { UnitMentionNode } from 'features/drilldown/panels/global/llmPanel/mentions/UnitMentionNode'
import { UnitMentionPlugin } from 'features/drilldown/panels/global/llmPanel/mentions/UnitMentionPlugin'
import { useMentionableEntries } from 'features/drilldown/panels/global/llmPanel/mentions/useMentionableEntries'
import { ChatInputProps } from 'features/drilldown/panels/global/llmPanel/types'
import {
  $createParagraphNode,
  $getRoot,
  $nodesOfType,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND,
  LexicalEditor,
} from 'lexical'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

const InputContainer = styled.div`
  padding: 16px;
  flex-shrink: 0;
`

const EditorShell = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`

const EditorSurface = styled.div`
  position: relative;
  flex: 1;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
  padding: 0.375rem 0.625rem;

  &:focus-within {
    border-color: ${({ theme }) => theme.forecast.primary};
  }
`

const StyledContentEditable = styled(ContentEditable)`
  min-height: 1.5rem;
  max-height: 8rem;
  overflow-y: auto;
  outline: none;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.875rem;
  line-height: 1.5rem;
  word-break: break-word;
  white-space: pre-wrap;
`

const Placeholder = styled.div`
  position: absolute;
  top: 0.375rem;
  left: 0.625rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
  line-height: 1.5rem;
  pointer-events: none;
  user-select: none;
`

/** Read the current plain text + the set of mentioned unit ids from the editor. */
function readEditorContent(editor: LexicalEditor): {
  text: string
  unitIds: string[]
} {
  let text = ''
  let unitIds: string[] = []
  editor.getEditorState().read(() => {
    text = $getRoot().getTextContent()
    unitIds = Array.from(
      new Set($nodesOfType(UnitMentionNode).map((node) => node.getUnitId()))
    )
  })
  return { text: text.trim(), unitIds }
}

function clearEditor(editor: LexicalEditor): void {
  editor.update(() => {
    const root = $getRoot()
    root.clear()
    const paragraph = $createParagraphNode()
    root.append(paragraph)
    paragraph.select()
  })
}

/**
 * Wires submit behavior (Enter-to-send, send button, empty-state disabling) and
 * keeps the editor's editable flag in sync with the loading/disabled state.
 * Rendered inside the composer so it can access the editor context.
 */
const EditorControls: FC<{
  onSend: (text: string, unitIds: string[]) => void
  isLoading: boolean
  disabled: boolean
}> = ({ onSend, isLoading, disabled }) => {
  const [editor] = useLexicalComposerContext()
  const [isEmpty, setIsEmpty] = useState(true)

  const blocked = isLoading || disabled

  const submit = useCallback(() => {
    if (blocked) return
    const { text, unitIds } = readEditorContent(editor)
    if (!text) return
    onSend(text, unitIds)
    clearEditor(editor)
  }, [blocked, editor, onSend])

  useEffect(() => {
    editor.setEditable(!blocked)
  }, [editor, blocked])

  // Track emptiness for the send button + Enter guard.
  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          setIsEmpty($getRoot().getTextContent().trim().length === 0)
        })
      }),
    [editor]
  )

  // Enter submits; Shift+Enter inserts a newline. Registered at HIGH priority so
  // it runs before PlainTextPlugin's built-in newline handler. When the @-mention
  // typeahead menu is open we defer to it (return false) so Enter selects the
  // highlighted asset instead of sending; Lexical assigns the open menu the
  // 'typeahead-menu' element id, which is removed on close.
  useEffect(
    () =>
      editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event: KeyboardEvent | null) => {
          if (document.getElementById('typeahead-menu')) return false
          if (event?.shiftKey) return false
          event?.preventDefault()
          submit()
          return true
        },
        COMMAND_PRIORITY_HIGH
      ),
    [editor, submit]
  )

  return (
    <Button
      type='primary'
      icon={<SendOutlined />}
      onClick={submit}
      disabled={isEmpty || blocked}
      loading={isLoading}>
      Send
    </Button>
  )
}

export const ChatInput: FC<ChatInputProps> = ({
  onSend,
  isLoading,
  disabled = false,
  orgId,
  siteId,
}) => {
  const { entries, isLoading: unitsLoading } = useMentionableEntries(
    orgId,
    siteId
  )

  const initialConfig = useMemo(
    () => ({
      namespace: 'LlmChatInput',
      nodes: [UnitMentionNode],
      theme: {},
      editable: !isLoading && !disabled,
      onError: (error: Error) => {
        console.error('LlmChatInput lexical error', error)
      },
    }),
    // Intentionally build once; editability is synced imperatively below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <InputContainer>
      <LexicalComposer initialConfig={initialConfig}>
        <EditorShell>
          <EditorSurface>
            <PlainTextPlugin
              contentEditable={<StyledContentEditable />}
              placeholder={
                <Placeholder>
                  Create visualizations or ask anything... (@ to mention an
                  asset)
                </Placeholder>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <UnitMentionPlugin entries={entries} isLoading={unitsLoading} />
          </EditorSurface>
          <EditorControls
            onSend={onSend}
            isLoading={isLoading}
            disabled={disabled}
          />
        </EditorShell>
      </LexicalComposer>
    </InputContainer>
  )
}
